// Dataset metadata is a JSON *string* on the dataset record whose contents are arbitrary.
// These helpers parse it, and edit it immutably so React sees new references.

export const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/**
 * Parse the stored metadata string.
 *
 * Never throws: returns `{ ok: false, error }` so the UI can show a broken value and let the
 * user repair it instead of blanking their metadata.
 *
 * @param {*} raw The value from the API (usually a string; tolerated as an object already).
 * @returns {{ok: true, value: any, empty: boolean} | {ok: false, error: string, value: null, empty: false}}
 */
export const parseMetadata = (raw) => {
  if (raw === undefined || raw === null || String(raw).trim() === "") {
    return { ok: true, value: {}, empty: true };
  }

  if (typeof raw === "object") {
    return { ok: true, value: raw, empty: false };
  }

  try {
    const value = JSON.parse(raw);
    if (value === undefined) {
      return { ok: true, value: {}, empty: true };
    }
    return { ok: true, value, empty: false };
  } catch (error) {
    return { ok: false, error: error.message, value: null, empty: false };
  }
};

/**
 * Serialise for saving. Compact on purpose: it round-trips cleanly through the API and keeps
 * unrelated whitespace changes out of the payload.
 */
export const serializeMetadata = (value) => JSON.stringify(value);

export const formatMetadata = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return "";
  }
};

export const getValueKind = (value) => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (typeof value === "object") return "object";
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  return "string";
};

export const VALUE_KINDS = ["string", "number", "boolean", "object", "array", "null"];

export const KIND_LABELS = {
  string: "Text",
  number: "Number",
  boolean: "True / False",
  object: "Object",
  array: "List",
  null: "Null",
};

/** Convert a value to another kind, picking a sensible representation. */
export const convertValue = (kind, value) => {
  switch (kind) {
    case "string":
      if (typeof value === "string") return value;
      if (value === null || value === undefined) return "";
      if (typeof value === "object") return formatMetadata(value);
      return String(value);
    case "number": {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    case "boolean":
      if (typeof value === "string") return value.trim().toLowerCase() === "true";
      return Boolean(value);
    case "null":
      return null;
    case "object":
      return isPlainObject(value) ? value : {};
    case "array":
      return Array.isArray(value) ? value : [];
    default:
      return value;
  }
};

/** A fresh value of the given kind, used when switching a field's type. */
export const defaultValueForKind = (kind) =>
  convertValue(kind, kind === "number" ? 0 : kind === "boolean" ? false : "");

export const getAtPath = (root, path = []) =>
  path.reduce((node, key) => (node === undefined || node === null ? undefined : node[key]), root);

const cloneShallow = (node) => (Array.isArray(node) ? [...node] : { ...node });

/** Immutable set at a path (array of keys / indexes). */
export const setAtPath = (root, path, value) => {
  if (!path || path.length === 0) return value;

  const [head, ...rest] = path;
  const next = cloneShallow(root ?? {});
  next[head] = setAtPath(root?.[head], rest, value);
  return next;
};

/** Immutable delete of an object key or array index. */
export const deleteAtPath = (root, path) => {
  if (!path || path.length === 0) return undefined;

  const [head, ...rest] = path;

  if (rest.length === 0) {
    if (Array.isArray(root)) {
      const copy = [...root];
      copy.splice(Number(head), 1);
      return copy;
    }
    const copy = { ...(root ?? {}) };
    delete copy[head];
    return copy;
  }

  const next = cloneShallow(root ?? {});
  next[head] = deleteAtPath(root?.[head], rest);
  return next;
};

/** Immutable rename of an object key, preserving key order. */
export const renameKeyAt = (root, path, newKey) => {
  if (!path || path.length === 0) return root;

  const [head, ...rest] = path;

  if (rest.length === 0) {
    if (Array.isArray(root) || !isPlainObject(root)) return root;
    if (head === newKey) return root;

    const entries = Object.entries(root);
    const index = entries.findIndex(([key]) => key === head);
    if (index === -1) return root;

    entries[index] = [newKey, root[head]];
    return Object.fromEntries(entries);
  }

  const next = cloneShallow(root ?? {});
  next[head] = renameKeyAt(root?.[head], rest, newKey);
  return next;
};

/** Make `desired` unique against the existing keys, so a rename can't silently merge. */
export const uniqueKey = (keys = [], desired) => {
  const base = String(desired ?? "").trim() || "key";
  if (!keys.includes(base)) return base;

  let suffix = 2;
  while (keys.includes(`${base}_${suffix}`)) {
    suffix += 1;
  }
  return `${base}_${suffix}`;
};

/** Append a new key to the object at `path`. */
export const appendKey = (root, path = [], value = "") => {
  const target = getAtPath(root, path);
  if (!isPlainObject(target)) return root;

  const key = uniqueKey(Object.keys(target), "new_key");
  return setAtPath(root, path, { ...target, [key]: value });
};

/** Append a new item to the array at `path`. */
export const appendItem = (root, path = [], value = "") => {
  const target = getAtPath(root, path);
  if (!Array.isArray(target)) return root;

  return setAtPath(root, path, [...target, value]);
};

/** Number of entries at the root, for the tab summary. */
export const countEntries = (value) => {
  if (Array.isArray(value)) return value.length;
  if (isPlainObject(value)) return Object.keys(value).length;
  return 0;
};

/**
 * One-line summary for compact table cells, e.g. "2 keys", "3 items", "invalid" or "—" when
 * nothing is set.
 */
export const summarizeMetadata = (raw) => {
  const parsed = parseMetadata(raw);

  if (!parsed.ok) return "invalid";
  if (parsed.empty) return "—";

  const kind = getValueKind(parsed.value);
  const count = countEntries(parsed.value);

  if (kind === "object" || kind === "array") {
    if (count === 0) return "empty";
    const noun = kind === "array" ? "item" : "key";
    return `${count} ${noun}${count === 1 ? "" : "s"}`;
  }

  return kind;
};
